import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { CareTab } from "./CareTab";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useClients } from "@/features/clients/hooks/useClients";
import { useZETenders } from "@/features/ze_tenders/hooks";
import {
  useProjectContacts,
  useProjectPermissionMutations,
  useProjectPermissions,
  useSetProjectClient,
  useSetSourceTender,
  useUpdateConnection,
  usePresets,
} from "../hooks";
import type { ClientBranchInfo, PowerProject, ProjectContact } from "../types";
import { isRegulacjaOperator } from "../operatorTemplates";
import { fmtDate } from "../utils";
import { SectionCard } from "./ui";

// ── Klient i oddział (read-only, dane z przedsięwzięcia) ─────────────────────
function InfoRow({ label, value, href, to }: { label: string; value?: string | null; href?: string; to?: string }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2">
      <dt className="section-label shrink-0">{label}</dt>
      <dd className="text-right text-xs font-mono text-[var(--color-text-primary)] break-words min-w-0">
        {to ? (
          <Link to={to} className="text-[var(--color-primary)] hover:underline">{value}</Link>
        ) : href ? (
          <a href={href} className="text-[var(--color-primary)] hover:underline" target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function InfoGroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-1.5 bg-[var(--color-bg-elevated)] font-mono text-[0.65rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
      {children}
    </div>
  );
}

function BranchRows({ branch }: { branch: ClientBranchInfo }) {
  const address = [branch.street, branch.city].filter(Boolean).join(", ");
  return (
    <>
      <InfoRow label="Nazwa" value={branch.name} />
      <InfoRow label="Adres" value={address} />
      <InfoRow label="Osoba kontaktowa" value={branch.contact_person} />
      <InfoRow label="E-mail" value={branch.contact_email} href={branch.contact_email ? `mailto:${branch.contact_email}` : undefined} />
      <InfoRow label="Telefon" value={branch.contact_phone} href={branch.contact_phone ? `tel:${branch.contact_phone}` : undefined} />
    </>
  );
}

// Separate form to reassign the przedsięwzięcie's client/branch — CEO / dyrektor
// techniczny only (mirrors the backend project-client gate).
function ClientBranchEditForm({ connection, onDone }: { connection: PowerProject; onDone: () => void }) {
  const { data: clients, isLoading } = useClients();
  const m = useSetProjectClient(connection.id);
  const [clientId, setClientId] = useState(connection.client_info?.id ?? "");
  const [branchId, setBranchId] = useState(connection.branch_info?.id ?? "");
  const branches = (clients ?? []).find((c) => c.id === clientId)?.branches_brief ?? [];

  return (
    <div className="border-t border-[var(--color-border-muted)] p-4 space-y-2">
      {isLoading ? (
        <p className="section-label">Wczytywanie klientów…</p>
      ) : (
        <>
          <label className="block">
            <span className="section-label">Klient</span>
            <select
              className="input-base mt-1 text-xs py-1.5"
              value={clientId}
              onChange={(e) => { setClientId(e.target.value); setBranchId(""); }}
            >
              <option value="">— brak klienta —</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="section-label">Oddział</span>
            <select
              className="input-base mt-1 text-xs py-1.5"
              value={branchId}
              disabled={!clientId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">— brak oddziału —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}{b.city ? ` (${b.city})` : ""}</option>
              ))}
            </select>
          </label>
          {m.isError && (
            <p className="text-xs text-[var(--color-primary)] font-mono">Nie udało się zapisać zmiany.</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
            <Button
              size="sm"
              loading={m.isPending}
              onClick={() =>
                m.mutate(
                  { client: clientId || null, client_branch: branchId || null },
                  { onSuccess: onDone },
                )
              }
            >
              Zapisz
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ClientBranchCard({ connection }: { connection: PowerProject }) {
  const user = useAuthStore((s) => s.user);
  // /clients is gated to director/coordinator (RoleGuard) — link only for them.
  const canOpenClient = user?.role === "director" || user?.role === "coordinator";
  // Reassigning the client/branch mirrors the backend IsTechnicalDirector gate.
  const canEditClient = !!user?.is_ceo || !!user?.is_technical_director;
  const [editing, setEditing] = useState(false);
  const client = connection.client_info;
  const branch = connection.branch_info;
  const subBranch = connection.sub_branch_info;

  return (
    <SectionCard
      title="Klient i oddział"
      bodyClassName="p-0"
      action={
        canEditClient && !editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Zmień</Button>
        ) : undefined
      }
    >
      {editing ? (
        <ClientBranchEditForm connection={connection} onDone={() => setEditing(false)} />
      ) : !client && !branch && !subBranch ? (
        <p className="section-label normal-case tracking-normal p-4">
          Brak przypisanego klienta i oddziału — uzupełnij je w danych przedsięwzięcia.
        </p>
      ) : (
        <dl className="divide-y divide-[var(--color-border-muted)]">
          {client && (
            <>
              <InfoGroupHeader>Klient</InfoGroupHeader>
              <InfoRow
                label="Nazwa"
                value={client.short_name ? `${client.name} (${client.short_name})` : client.name}
                to={canOpenClient && client.id ? `/clients/${client.id}` : undefined}
              />
              <InfoRow label="NIP" value={client.nip} />
              <InfoRow label="Adres" value={client.address} />
              <InfoRow label="Osoba kontaktowa" value={client.contact_person} />
              <InfoRow label="E-mail" value={client.contact_email} href={client.contact_email ? `mailto:${client.contact_email}` : undefined} />
              <InfoRow label="Telefon" value={client.contact_phone} href={client.contact_phone ? `tel:${client.contact_phone}` : undefined} />
              <InfoRow label="WWW" value={client.website} href={client.website || undefined} />
            </>
          )}
          {branch && (
            <>
              <InfoGroupHeader>Oddział</InfoGroupHeader>
              <BranchRows branch={branch} />
            </>
          )}
          {subBranch && (
            <>
              <InfoGroupHeader>Pododdział</InfoGroupHeader>
              <BranchRows branch={subBranch} />
            </>
          )}
        </dl>
      )}
    </SectionCard>
  );
}

// Form to link/unlink the source ZE tender — CEO / dyrektor techniczny only
// (mirrors the backend source-tender gate).
function SourceTenderEditForm({ connection, onDone }: { connection: PowerProject; onDone: () => void }) {
  const { data: tenders, isLoading } = useZETenders();
  const m = useSetSourceTender(connection.id);
  const [tenderId, setTenderId] = useState(connection.source_tender ?? "");

  return (
    <div className="border-t border-[var(--color-border-muted)] p-4 space-y-2">
      {isLoading ? (
        <p className="section-label">Wczytywanie przetargów…</p>
      ) : (
        <>
          <SearchableSelect
            label="Przetarg ZE"
            placeholder="— brak powiązania —"
            value={tenderId}
            onChange={(e) => setTenderId(e.target.value)}
            options={(tenders ?? []).map((t) => ({
              value: t.id,
              label: t.reference_number ? `${t.title} (${t.reference_number})` : t.title,
            }))}
          />
          {m.isError && (
            <p className="text-xs text-[var(--color-primary)] font-mono">Nie udało się zapisać zmiany.</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
            <Button
              size="sm"
              loading={m.isPending}
              onClick={() => m.mutate(tenderId || null, { onSuccess: onDone })}
            >
              Zapisz
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// Source ZE tender card: read-only info for everyone; CEO / dyrektor techniczny can
// link/relink (hidden entirely for others when there is no link yet).
function SourceTenderCard({ connection }: { connection: PowerProject }) {
  const user = useAuthStore((s) => s.user);
  const canEdit = !!user?.is_ceo || !!user?.is_technical_director;
  const [editing, setEditing] = useState(false);
  const info = connection.source_tender_info;
  if (!info && !canEdit) return null;

  return (
    <SectionCard
      title="Przetarg ZE (źródło)"
      bodyClassName="p-0"
      action={
        canEdit && !editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            {info ? "Zmień" : "Powiąż"}
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <SourceTenderEditForm connection={connection} onDone={() => setEditing(false)} />
      ) : info ? (
        <dl className="divide-y divide-[var(--color-border-muted)]">
          <InfoRow label="Przetarg" value={info.title} to={`/ze-tenders/${info.id}`} />
          <InfoRow label="Nr referencyjny" value={info.reference_number} />
          <InfoRow label="Operator" value={info.operator_name} />
          <InfoRow label="Warianty mapy" value={String(info.variant_count)} />
        </dl>
      ) : (
        <p className="section-label normal-case tracking-normal p-4">
          Brak powiązanego przetargu ZE — powiąż go, aby kopiować mapę przetargu
          jako warianty trasy w zakładce Mapa.
        </p>
      )}
    </SectionCard>
  );
}

// Native contract fields (deadline excluded: it comes from the linked Project; contacts
// rendered separately below with autocomplete from the shared phone book).
const NATIVE_FIELDS: { key: keyof PowerProject; label: string; type?: string }[] = [
  { key: "contract_number", label: "Nr umowy" },
  { key: "st_number", label: "Nr ST" },
  { key: "wp_number", label: "Nr WP" },
  { key: "contract_signed_at", label: "Data zawarcia", type: "date" },
];

// The two contact triplets (z umowy / z ST-WP). Each feeds + is suggested from the
// shared "książka telefoniczna" (ProjectContact).
const CONTACT_GROUPS: {
  title: string;
  nameKey: keyof PowerProject;
  phoneKey: keyof PowerProject;
  emailKey: keyof PowerProject;
}[] = [
  { title: "Kontakt z umowy", nameKey: "contract_contact_name", phoneKey: "contract_contact_phone", emailKey: "contract_contact_email" },
  { title: "Kontakt z ST / WP", nameKey: "st_contact_name", phoneKey: "st_contact_phone", emailKey: "st_contact_email" },
];

// Autocomplete over the phone book: type a name (or phone/e-mail) to pick an existing
// person — fills all three fields — or just type a new one (it lands in the base on save).
function ContactNameAutocomplete({
  value,
  onChange,
  onPick,
  contacts,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (c: ProjectContact) => void;
  contacts: ProjectContact[];
}) {
  const [open, setOpen] = useState(false);
  const q = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return contacts
      .filter((c) =>
        [c.name, c.phone, c.email, c.position].some((f) => f?.toLowerCase().includes(q)),
      )
      .slice(0, 8);
  }, [contacts, q]);

  return (
    <div className="relative">
      <input
        type="text"
        className="input-base text-xs w-52 py-1"
        value={value}
        placeholder="Imię i nazwisko"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <ul className="absolute right-0 z-20 mt-1 w-64 max-h-56 overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-lg text-left">
          {matches.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left hover:bg-[var(--color-bg-elevated)]"
                onMouseDown={(e) => { e.preventDefault(); onPick(c); setOpen(false); }}
              >
                <span className="text-xs font-medium text-[var(--color-text-primary)]">{c.name}</span>
                <span className="block text-[10px] font-mono text-[var(--color-text-muted)]">
                  {[c.phone, c.email, c.position].filter(Boolean).join(" · ") || "—"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContactGroup({
  group,
  form,
  setField,
  contacts,
}: {
  group: (typeof CONTACT_GROUPS)[number];
  form: Record<string, string>;
  setField: (key: string, value: string) => void;
  contacts: ProjectContact[];
}) {
  const pick = (c: ProjectContact) => {
    setField(group.nameKey as string, c.name);
    setField(group.phoneKey as string, c.phone);
    setField(group.emailKey as string, c.email);
  };
  return (
    <>
      <InfoGroupHeader>{group.title}</InfoGroupHeader>
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <dt className="section-label">Osoba</dt>
        <dd className="text-right">
          <ContactNameAutocomplete
            value={form[group.nameKey as string]}
            onChange={(v) => setField(group.nameKey as string, v)}
            onPick={pick}
            contacts={contacts}
          />
        </dd>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <dt className="section-label">Telefon</dt>
        <dd className="text-right">
          <input
            type="text"
            className="input-base text-xs w-52 py-1"
            value={form[group.phoneKey as string]}
            onChange={(e) => setField(group.phoneKey as string, e.target.value)}
          />
        </dd>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <dt className="section-label">E-mail</dt>
        <dd className="text-right">
          <input
            type="email"
            className="input-base text-xs w-52 py-1"
            value={form[group.emailKey as string]}
            onChange={(e) => setField(group.emailKey as string, e.target.value)}
          />
        </dd>
      </div>
    </>
  );
}

// Task-level source data for the OMI regulatory application (kontekst regulatory_application).
const REG_FIELD_KEYS: (keyof PowerProject)[] = [
  "connection_group",
  "omi_responsible_name",
  "td_order_date",
  "psp_zadania",
];

// ── Uprawnienia per projekt (zatwierdzanie / składanie wniosków) ─────────────
// Replaces the old per-author "weryfikacja pism" policy: a director or the
// przedsięwzięcie's coordinator grants, per project and per user, the APPROVE
// and/or SUBMIT gates on proceeding applications.
function ProjectPermissionsPanel({ connection }: { connection: PowerProject }) {
  const { data: permissions } = useProjectPermissions(connection.id);
  const { data: users } = useInternalUsers();
  const m = useProjectPermissionMutations(connection.id);
  const [addUser, setAddUser] = useState("");

  const userName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users ?? []) map[u.id] = u.full_name || u.username;
    return map;
  }, [users]);

  // Users not yet granted any permission — candidates for the "add" picker.
  const granted = new Set((permissions ?? []).map((p) => p.user));
  const addOptions = (users ?? [])
    .filter((u) => !granted.has(u.id))
    .map((u) => ({ value: u.id, label: u.full_name || u.username }));

  const rows = permissions ?? [];

  return (
    <SectionCard
      title="Uprawnienia projektu — zatwierdzanie / składanie wniosków"
      action={
        addOptions.length > 0 ? (
          <div className="w-56">
            <SearchableSelect
              options={addOptions}
              value={addUser}
              placeholder="+ Dodaj osobę…"
              onChange={(e) => {
                const id = e.target.value;
                if (!id) return;
                m.set.mutate({ user: id, can_approve: false, can_submit: false });
                setAddUser("");
              }}
            />
          </div>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <p className="section-label normal-case tracking-normal">
          Brak nadanych uprawnień. Dyrektor i koordynator przedsięwzięcia mają uprawnienia
          domyślnie; dodaj osobę, by przyznać zatwierdzanie / składanie.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 py-1.5 border-b border-[var(--color-border-muted)] last:border-0">
              <span className="font-medium text-sm text-[var(--color-text-primary)] min-w-[140px]">
                {p.user_name || userName[p.user] || p.user}
              </span>
              <label className="flex items-center gap-1.5 font-mono text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={p.can_approve}
                  disabled={m.set.isPending}
                  onChange={(e) => m.set.mutate({ user: p.user, can_approve: e.target.checked, can_submit: p.can_submit })}
                />
                Zatwierdzanie
              </label>
              <label className="flex items-center gap-1.5 font-mono text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={p.can_submit}
                  disabled={m.set.isPending}
                  onChange={(e) => m.set.mutate({ user: p.user, can_approve: p.can_approve, can_submit: e.target.checked })}
                />
                Składanie
              </label>
              <button
                className="ml-auto text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                title="Usuń uprawnienia"
                onClick={() => m.remove.mutate(p.id)}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="section-label normal-case tracking-normal mt-3 pt-3 border-t border-[var(--color-border-muted)]">
        Zatwierdzanie ≠ składanie — to dwa odrębne uprawnienia bramkujące cykl życia wniosku (DRAFT → ZATWIERDZONY → ZŁOŻONY).
      </p>
    </SectionCard>
  );
}

// All contact triplet keys, derived from CONTACT_GROUPS (kept in form state + payload).
const CONTACT_KEYS = CONTACT_GROUPS.flatMap((g) => [g.nameKey, g.phoneKey, g.emailKey]);

export function MetadataTab({ connection }: { connection: PowerProject }) {
  const updateConn = useUpdateConnection(connection.id);
  const { data: presets } = usePresets();
  const { data: contacts } = useProjectContacts();
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director" || !!user?.is_ceo;
  // Koordynator przedsięwzięcia zarządza uprawnieniami swojego projektu jak dyrektor
  // (lustro backendowego _is_pm_or_director w ProjectAccessPermissionViewSet).
  const canManagePermissions =
    isDirector || (!!user && (connection.project_coordinators ?? []).includes(user.id));

  const [form, setForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      [...NATIVE_FIELDS.map((f) => f.key), ...CONTACT_KEYS, ...REG_FIELD_KEYS].map((key) => [
        key,
        (connection[key] as string) || "",
      ]),
    ),
  );
  const setField = (key: string, value: string) => setForm((s) => ({ ...s, [key]: value }));
  // Regulacja (TD Oddział Gliwice) dotyczy tylko TAURON-a — w projekcie ENERGA sekcja
  // znika, chyba że ktoś zdążył już coś w nią wpisać (wtedy nie chowamy danych).
  const showRegulacja =
    isRegulacjaOperator(connection.operator) ||
    REG_FIELD_KEYS.some((k) => !!connection[k]);
  const [rows, setRows] = useState<{ label: string; value: string }[]>(
    () => (connection.extra_metadata ?? []).map((r) => ({ ...r })),
  );

  const save = () => {
    const payload: Record<string, unknown> = {
      ...form,
      extra_metadata: rows.filter((r) => r.label.trim()),
    };
    // Empty date string → null (DRF DateField rejects "").
    if (!payload.contract_signed_at) payload.contract_signed_at = null;
    if (!payload.td_order_date) payload.td_order_date = null;
    updateConn.mutate(payload as never);
  };

  // Apply a preset = append its columns (that aren't already present) as empty rows.
  const applyPreset = (id: string) => {
    const preset = (presets ?? []).find((p) => p.id === id);
    if (!preset) return;
    setRows((prev) => {
      const have = new Set(prev.map((r) => r.label.toLowerCase()));
      const added = preset.columns
        .filter((c) => !have.has(c.toLowerCase()))
        .map((label) => ({ label, value: "" }));
      return [...prev, ...added];
    });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5 items-start">
      <SectionCard
        title="Metadane umowy (preset bazowy)"
        bodyClassName="p-0"
        action={
          <Link
            to="/power-projects/contacts"
            className="font-mono text-[0.65rem] uppercase tracking-widest text-[var(--color-primary)] hover:underline"
          >
            Książka telefoniczna
          </Link>
        }
      >
        <dl className="divide-y divide-[var(--color-border-muted)]">
          <div className="flex items-center justify-between gap-3 px-4 py-2">
            <dt className="section-label">Termin umowy</dt>
            <dd className="text-right text-xs font-mono">
              <span className="text-[var(--color-text-primary)]">{fmtDate(connection.contract_deadline)}</span>
              <span className="block text-[10px] text-[var(--color-text-muted)] normal-case">z przedsięwzięcia</span>
            </dd>
          </div>
          {NATIVE_FIELDS.map((f) => (
            <div key={f.key as string} className="flex items-center justify-between gap-3 px-4 py-2">
              <dt className="section-label">{f.label}</dt>
              <dd className="text-right">
                {f.type === "date" ? (
                  <DatePicker
                    className="w-52 py-1"
                    value={form[f.key as string]}
                    onChange={(v) => setField(f.key as string, v)}
                    aria-label={f.label}
                  />
                ) : (
                  <input
                    type={f.type || "text"}
                    className="input-base text-xs w-52 py-1"
                    value={form[f.key as string]}
                    onChange={(e) => setField(f.key as string, e.target.value)}
                  />
                )}
              </dd>
            </div>
          ))}
          {CONTACT_GROUPS.map((g) => (
            <ContactGroup key={g.title} group={g} form={form} setField={setField} contacts={contacts ?? []} />
          ))}
        </dl>
      </SectionCard>

      {showRegulacja && (
        <SectionCard title="Wniosek o regulację - TD Oddział Gliwice" bodyClassName="p-0">
          <dl className="divide-y divide-[var(--color-border-muted)]">
            {!isRegulacjaOperator(connection.operator) && (
              <div className="px-4 py-2">
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  Procedura TAURON — ten projekt prowadzi {connection.operator_display || "inny operator"}.
                  Sekcja jest widoczna tylko dlatego, że są w niej wpisane dane.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 px-4 py-2">
              <dt className="section-label">Grupa przyłączeniowa</dt>
              <dd className="text-right">
                <select
                  className="input-base text-xs w-52 py-1"
                  value={form.connection_group}
                  onChange={(e) => setForm((s) => ({ ...s, connection_group: e.target.value }))}
                >
                  <option value="">—</option>
                  <option value="III">III</option>
                  <option value="IV">IV</option>
                  <option value="V">V</option>
                </select>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-2">
              <dt className="section-label">Osoba odpowiedzialna w OMI</dt>
              <dd className="text-right">
                <input
                  type="text"
                  className="input-base text-xs w-52 py-1"
                  value={form.omi_responsible_name}
                  onChange={(e) => setForm((s) => ({ ...s, omi_responsible_name: e.target.value }))}
                />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-2">
              <dt className="section-label">Data otrzymania zlecenia od TD</dt>
              <dd className="text-right">
                <DatePicker
                  className="w-52 py-1"
                  value={form.td_order_date}
                  onChange={(v) => setForm((s) => ({ ...s, td_order_date: v }))}
                />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-2">
              <dt className="section-label">PSP zadania (TAURON)</dt>
              <dd className="text-right">
                <input
                  className="input-base w-52 py-1"
                  value={form.psp_zadania}
                  placeholder="np. I-GL-AI-2504550"
                  onChange={(e) => setForm((s) => ({ ...s, psp_zadania: e.target.value }))}
                />
              </dd>
            </div>
            <div className="px-4 py-2">
              <p className="text-[11px] text-[var(--color-text-muted)]">
                Opisy urządzeń projektowanych i istniejących wpisuje się teraz per
                działka — w zakładce „Działki”.
              </p>
            </div>
          </dl>
        </SectionCard>
      )}

      <SectionCard
        title="Kolumny dodatkowe"
        action={
          (presets ?? []).length > 0 ? (
            <select
              className="input-base text-[11px] py-0.5 w-auto"
              value=""
              onChange={(e) => e.target.value && applyPreset(e.target.value)}
            >
              <option value="">Zastosuj preset…</option>
              {(presets ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : isDirector ? (
            <Link to="/power-projects/presety?tab=metadane" className="font-mono text-[0.65rem] uppercase tracking-widest text-[var(--color-primary)] hover:underline">
              Utwórz preset
            </Link>
          ) : null
        }
      >
        <div className="space-y-2">
          {rows.length === 0 && (
            <p className="section-label normal-case tracking-normal">
              Brak własnych kolumn. Dodaj kolumnę lub zastosuj preset.
            </p>
          )}
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="input-base text-xs py-1 w-40"
                placeholder="Nazwa kolumny"
                value={r.label}
                onChange={(e) => setRows((s) => s.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <input
                className="input-base text-xs py-1 flex-1"
                placeholder="Wartość"
                value={r.value}
                onChange={(e) => setRows((s) => s.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
              />
              <button
                className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                onClick={() => setRows((s) => s.filter((_, j) => j !== i))}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setRows((s) => [...s, { label: "", value: "" }])}>
            + Dodaj kolumnę
          </Button>
        </div>

        {isDirector && (
          <p className="section-label normal-case tracking-normal mt-3 pt-3 border-t border-[var(--color-border-muted)]">
            Presety (szablony kolumn) definiujesz w{" "}
            <Link to="/power-projects/presety?tab=metadane" className="text-[var(--color-primary)] hover:underline">
              Presety metadanych
            </Link>
            .
          </p>
        )}
      </SectionCard>

      <ClientBranchCard connection={connection} />

      <SourceTenderCard connection={connection} />

      <div className="lg:col-span-2 flex flex-wrap items-center justify-between gap-3">
        <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
          zapisuje metadane umowy i wniosku o regulację — sekcja „Opieka” niżej ma własne zapisy
        </span>
        <span className="ml-auto inline-flex items-center gap-3">
          {updateConn.isSuccess && <span className="text-xs font-mono text-emerald-600">Zapisano.</span>}
          <Button loading={updateConn.isPending} onClick={save}>Zapisz metadane</Button>
        </span>
      </div>

      {/* Opieka — karta specyfiki klienta (dawna zakładka „Opieka"): flagi „trudnego
          klienta", ustalenia specjalne, dziennik obserwacji. Wpisy żyją na poziomie
          klient / oddział / pododdział i zapisują się same, poza „Zapisz metadane" —
          stąd osobny blok pod grubą kreską, z bursztynowym paskiem karty. */}
      <div className="lg:col-span-2 mt-2 border-t-2 border-[var(--color-border-strong)] pt-5" id="opieka">
        <p className="section-label mb-2">Dane wewnętrzne GK ELEKTIM — poziom klienta / oddziału, nie projektu</p>
        <SectionCard
          className="border-[var(--color-border-strong)] border-l-4 border-l-amber-500"
          title={
            <span className="inline-flex items-center gap-2 text-[var(--color-text-primary)]">
              <ShieldAlert size={13} className="text-amber-600" /> Opieka — karta specyfiki klienta
            </span>
          }
        >
          <CareTab connection={connection} />
        </SectionCard>
      </div>

      {/* Per-project approve/submit gate (replaces verification policy) — director or
          przedsięwzięcie coordinator. */}
      {canManagePermissions && (
        <div className="lg:col-span-2">
          <ProjectPermissionsPanel connection={connection} />
        </div>
      )}
    </div>
  );
}
